import { type ReactNode, useState, useTransition } from "react";
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  Heading,
  Modal,
  ModalOverlay,
} from "react-aria-components";
import { Button } from "./Button";
import buttonStyles from "./Button.module.css";
import styles from "./ConfirmDialog.module.css";

// A refusal the data raises rather than the form: archiving a template can be
// blocked by rules spanning other rows, and the dialog is where the reader
// already is. Returning nothing means it went through.
export type ConfirmRefusal = { refusal: string } | null;

type ConfirmDialogProps = {
  triggerLabel: string;
  triggerIcon?: ReactNode;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  pendingLabel: string;
  isDestructive?: boolean;
  confirmAction: () => Promise<ConfirmRefusal>;
};

// The trigger reads as a normal row control; only the confirming click inside
// the dialog is styled as destructive.
export const ConfirmDialog = ({
  triggerLabel,
  triggerIcon,
  title,
  body,
  confirmLabel,
  pendingLabel,
  isDestructive,
  confirmAction,
}: ConfirmDialogProps) => {
  const [isPending, startTransition] = useTransition();
  const [refusal, setRefusal] = useState<string | null>(null);

  const handleConfirm = (close: () => void) => {
    startTransition(async () => {
      const result = await confirmAction();

      // A refused action leaves the dialog open: closing it would hide the
      // only explanation of why nothing happened.
      if (result !== null) {
        setRefusal(result.refusal);

        return;
      }

      close();
    });
  };

  return (
    <DialogTrigger>
      {/* DialogTrigger opens its dialog through a PressResponder, which a plain
          <button> ignores — the trigger has to be the react-aria one, wearing
          the shared button styles. */}
      <AriaButton className={buttonStyles.button} data-variant="ghost">
        {triggerIcon}
        {triggerLabel}
      </AriaButton>
      <ModalOverlay className={styles.overlay} isDismissable={!isPending}>
        <Modal className={styles.modal}>
          <Dialog className={styles.dialog} role="alertdialog">
            {({ close }) => (
              <>
                <Heading slot="title" className={styles.title}>
                  {title}
                </Heading>
                <div className={styles.body}>{body}</div>
                {refusal === null ? null : (
                  <p className={styles.refusal} role="alert">
                    {refusal}
                  </p>
                )}
                <div className={styles.actions}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={close}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant={isDestructive ? "danger" : "primary"}
                    onClick={() => handleConfirm(close)}
                    disabled={isPending}
                  >
                    {isPending ? pendingLabel : confirmLabel}
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
